'use client';

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';

interface NavigationContextValue {
  loadingHref: string | null;
  navKey: number;
  startLoading: (href: string) => void;
  stopLoading: () => void;
  navigate: (href: string) => void;
}

const NavigationContext = createContext<NavigationContextValue>({
  loadingHref: null,
  navKey: 0,
  startLoading: () => {},
  stopLoading: () => {},
  navigate: () => {},
});

export function NavigationProvider({ children, }: { children: React.ReactNode }) {
  const [loadingHref, setLoadingHref,] = useState<string | null>(null);
  const [navKey, setNavKey,] = useState(0);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    setLoadingHref(null);
    document.body.style.overflow = ``;
    document.documentElement.style.overflow = ``;
  }, [pathname,]);

  const startLoading = useCallback((href: string) => {
    setLoadingHref(href);
    setNavKey(k => k + 1);
  }, []);

  const stopLoading = useCallback(() => {
    setLoadingHref(null);
  }, []);

  const navigate = useCallback((href: string) => {
    if (href === pathname) {
      const hamburger = document.querySelector(`.hamburger`);
      const nav = document.querySelector(`.hamburger-menu`);
      if (hamburger) hamburger.classList.remove(`is-active`);
      if (nav) nav.classList.remove(`open`);
      document.body.style.overflow = ``;
      document.documentElement.style.overflow = ``;
      return;
    }
    setLoadingHref(href);
    setNavKey(k => k + 1);
    router.push(href);
  }, [router, pathname,]);

  return (
    <NavigationContext.Provider value={{ loadingHref, navKey, startLoading, stopLoading, navigate, }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  return useContext(NavigationContext);
}
